import { MessageSegment } from "@/components/AI/chat/utils/messages";
import { estimateTokenCount } from "tokenx";

/**
 * Counts tokens from parsed message segments for eligible block types:
 * - reasoning
 * - aiChanges (ai-changes)
 * - code-edit-stream
 * - text
 * This cover all the segment types with AI responses. Segment like vercel-deployment-message, env-variables-input, etc. are not counted.
 * @param segments The parsed message segments from parseMessageContentBlocks
 * @returns Number of tokens in eligible blocks
 */
export const countEligibleTokensFromSegments = (
  segments: MessageSegment[]
): number => {
  if (!segments || !Array.isArray(segments)) {
    return 0;
  }

  let tokenCount = 0;

  // Filter for eligible segment types
  const eligibleSegments = segments.filter(
    (segment) =>
      segment.type === "reasoning" ||
      segment.type === "aiChanges" ||
      segment.type === "code-edit-stream" ||
      segment.type === "text"
  );

  eligibleSegments.forEach((segment) => {
    let contentToCount = "";

    switch (segment.type) {
      case "reasoning":
        // For reasoning, count from parsedJson if available, otherwise fallback to content
        if (segment.parsedJson && !segment.jsonIsBroken) {
          // Convert parsed JSON to string for token counting
          contentToCount =
            typeof segment.parsedJson === "string"
              ? segment.parsedJson
              : JSON.stringify(segment.parsedJson);
        } else if (segment.content) {
          // Fallback to raw content, removing XML tags
          contentToCount = segment.content.replace(/<\/?reasoning>/g, "");
        }
        break;

      case "aiChanges":
        // For ai-changes, count from aiChanges object if available
        if (segment.aiChanges && !segment.jsonIsBroken) {
          contentToCount = JSON.stringify(segment.aiChanges);
        } else if (segment.content) {
          // Fallback to raw content, removing XML tags
          contentToCount = segment.content.replace(/<\/?ai-changes>/g, "");
        }
        break;

      case "code-edit-stream":
      case "text":
        contentToCount = segment.content || "";
        break;
    }

    if (contentToCount) {
      tokenCount += estimateTokenCount(contentToCount);
    }
  });

  return tokenCount;
};
